import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@tdelivr/api-client";

import { deviceIdentity, nextSequence } from "../device.ts";
import type {
  AddressRecord,
  DeliveryOrderRecord,
  DistanceEtaResult,
  DriverApi,
} from "../driver-api.ts";
import {
  ADDRESS_READ_SCOPE,
  ARRIVED_AT_PICKUP,
  ARRIVED_REASON,
  ARRIVAL_CONFLICT_SUB,
  ARRIVAL_CONFLICT_TITLE,
  arrivalUnavailable,
  CONTACT_ABSENCE,
  NO_ORDER_SUB,
  NO_ORDER_TITLE,
  ORDER_READ_SCOPE,
  ORDER_TRANSITION_SCOPE,
  PICKUP_ADDRESS_UNREADABLE,
  PICKUP_TRAVEL_MODE,
  pickupChip,
  pickupIsDesigned,
  pickupSubLine,
} from "../pickup.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `4 To pickup` — where the parcel is, how far it is, and the slider that says you are there.
 *
 * The design: the **map** hero, drawn as its 210px band of `C.map` with nothing in it for the reason
 * `PhoneShell.tsx`'s closing note gives and Q-68 records; the `['active','to pickup']` chip;
 * _"Masaki Mall, loading bay"_; _"1.2 km · 4 min · background location on"_; the one row
 * `Contact · Merchant desk (via support)`; and the slide-to-confirm `I've arrived`.
 *
 * `src/pickup.ts` carries the reasoning for every value, for the one absence the mobile sweep marks
 * this screen degraded on, and for the two values that are this build's rather than the platform's.
 *
 * ## Which delivery, and why it has to be told
 *
 * DEL-03's listing is explicitly an operator's board — it requires `delivery:order:workload` "in
 * addition to delivery:order:read", and its own description says narrowing it to the caller's own
 * orders "would make one route answer two questions depending on who asked". A courier's token
 * cannot ask it, and nothing else publishes the delivery a courier is on. So the identifier is read
 * from the address — `?order=<uuid>` — which is the arrangement `8 Proof` has for `?proof=` and
 * screen 2 has for `?offers=`, and for the same reason: a push notification through NTF would open
 * the application at it. R-33 and Q-77.
 *
 * ## Four requests, and three of them are allowed to fail alone
 *
 * The order is the subject: without it there is no screen. The address, this device's position and
 * GEO-04's answer each take a piece of the headline and the sub-line with them if they fail, and
 * take nothing else — because a courier who can see which delivery they are on and not how far it
 * is has lost a line, and a courier shown an error screen has lost the job.
 */

export interface ToPickupProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  readonly driverId: string | undefined;
  /** The delivery this screen was told about, from the address. Absent is a state, not an error. */
  readonly orderId: string | undefined;
  /** This device's position, injected so the acceptance check and the tests can drive it. */
  readonly position: () => Promise<GeolocationPosition>;
  readonly now: () => number;
}

interface Loaded {
  readonly order: DeliveryOrderRecord;
  readonly address: AddressRecord | undefined;
  readonly distance: DistanceEtaResult | undefined;
  readonly locationSent: boolean;
  readonly locationRefused: boolean;
}

export function ToPickup(props: ToPickupProps): ReactNode {
  const { api, tenantId, driverId, orderId } = props;
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [conflicted, setConflicted] = useState(false);
  const [arriving, setArriving] = useState(false);
  /*
   * Kept across renders so a reload does not re-issue the sequence this device already spent.
   *
   * `nextSequence` persists before it is used, so this is a cache rather than the record — but
   * re-reading it on every render would spend a number per render, and DRV-06 would see a sequence
   * racing ahead of the positions actually sent.
   */
  const device = useRef(deviceIdentity());

  const load = useCallback(() => {
    if (orderId === undefined) return;
    setState({ kind: "loading" });
    /*
     * The conflict flag is **not** cleared here, and that was a defect before it was a comment.
     *
     * A 409 reloads the order, and the reload is the thing that answers the conflict — so clearing
     * the flag on the way in meant the screen came back drawing `to pickup` over an order that had
     * moved, with no trace that the courier's slide had been refused. The flag is cleared when the
     * slider is next pressed instead, which is the moment the courier has seen it.
     */
    api
      .deliveryOrder({ tenantId, orderId })
      .then(async (order) => {
        const address = await api.address(order.pickupAddressId).catch(() => undefined);
        const leg = await legFor(api, props.position, device.current.deviceId, driverId, address);
        setLoaded({ order, address, ...leg });
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => {
        setState(
          stateForError(error, [ORDER_READ_SCOPE, ORDER_TRANSITION_SCOPE, ADDRESS_READ_SCOPE]),
        );
      });
    // `props.position` is a prop and is deliberately not a dependency: a new function identity on
    // every render would reload the whole screen four times a second.
  }, [api, tenantId, orderId, driverId]);

  useEffect(load, [load]);

  /**
   * The slider, and the one thing it must not do on a 409.
   *
   * A conflict means the order moved underneath this screen. The correct answer is to **read** the
   * order again and draw what it says — never to retry the command, which is `CLAUDE.md` section 2's
   * rule that a "check status" action is a read. So the 409 sets a flag and reloads; it does not
   * press the transition a second time at the version the service just named.
   */
  const arrive = useCallback(() => {
    if (orderId === undefined || loaded === undefined || arriving) return;
    setArriving(true);
    setConflicted(false);
    api
      .transitionDeliveryOrder({
        tenantId,
        orderId,
        toStatus: ARRIVED_AT_PICKUP,
        expectedVersion: loaded.order.version,
        occurredAt: new Date(props.now()).toISOString(),
        reason: ARRIVED_REASON,
      })
      .then((order) => {
        setLoaded((held) => (held === undefined ? held : { ...held, order }));
        setArriving(false);
      })
      .catch((error: unknown) => {
        setArriving(false);
        if (error instanceof ApiError && error.category === "conflict") {
          setConflicted(true);
          load();
          return;
        }
        setState(stateForError(error, [ORDER_TRANSITION_SCOPE]));
      });
  }, [api, tenantId, orderId, loaded, arriving, load]);

  if (orderId === undefined) {
    return (
      <StateScreen
        screen="pickup"
        chip={{ class: "unknown", label: "no delivery named" }}
        title={NO_ORDER_TITLE}
        sub={NO_ORDER_SUB}
      />
    );
  }

  if (state.kind !== "ready" || loaded === undefined) {
    return (
      <StateScreen
        screen="pickup"
        state={state}
        onRetry={load}
        purpose="Looking up where you're collecting from."
      />
    );
  }

  const sub = pickupSubLine({
    distance: loaded.distance,
    locationSent: loaded.locationSent,
    locationRefused: loaded.locationRefused,
  });
  const unavailable = arrivalUnavailable(loaded.order.status);
  const designed =
    !conflicted &&
    sub.designed &&
    pickupIsDesigned(loaded.order.status) &&
    loaded.address !== undefined;

  return (
    <PhoneShell
      screen="pickup"
      /*
       * `map:1`. The hero is the design's flat `C.map` band at its own height and nothing is drawn
       * in it — the design builds five map layers for this screen and assigns `layers` twice, so
       * none of them reaches the page in the design file **or** in the approved reference
       * screenshot. Both artefacts agree, which is what settles it. Q-68.
       */
      map
      /* `on:1`. A courier on the way to a pickup is on shift, which is the design's own flag. */
      online
      earningsLabel={null}
      chip={
        conflicted ? { class: "human", label: "action_required" } : pickupChip(loaded.order.status)
      }
      title={
        conflicted
          ? ARRIVAL_CONFLICT_TITLE
          : (loaded.address?.formattedAddress ?? PICKUP_ADDRESS_UNREADABLE)
      }
      {...(loaded.address === undefined && !conflicted
        ? {
            titleAbsence:
              "GEO-02 holds this pickup's addressId and the read of it did not answer, so the place is not drawn.",
          }
        : {})}
      sub={conflicted ? ARRIVAL_CONFLICT_SUB : sub.text}
      rows={[
        /*
         * The design's one row, drawn with its key and without a value.
         *
         * The mobile sweep's single finding against this screen. Never a telephone number, never a
         * name, never a dash: the dashed `?` pill this application uses for everything the platform
         * did not publish, with the reason on it.
         */
        { key: "Contact", value: null, because: CONTACT_ABSENCE },
      ]}
      actions={[
        {
          label: "I've arrived",
          glyph: "where_to_vote",
          primary: true,
          /*
           * `slide`, which is the design's fourth element on this button. Its notes:
           * _"slide-to-confirm for accept, arrive, start and complete so a pocket tap can't trigger
           * them."_ Arriving is the second of those four.
           */
          slide: true,
          ...(unavailable === undefined ? { onPress: arrive } : { unavailable }),
        },
      ]}
      {...(designed ? {} : { flag: "wording not in the design" })}
    />
  );
}

/**
 * This device's position, sent to DRV-06, and the distance from it to the pickup.
 *
 * In that order and with that dependency, which is the design's own sentence read as a sequence:
 * `1.2 km · 4 min · background location on` is one claim about a position and two about a route from
 * it. A distance computed from a position this application never reported would make the third
 * phrase false while the first two looked fine.
 *
 * Every step is caught separately. A browser that refuses geolocation, a DRV-06 that refuses the
 * write and a GEO-04 that has no provider are three different facts, and the sub-line says which.
 */
async function legFor(
  api: DriverApi,
  position: () => Promise<GeolocationPosition>,
  deviceId: string,
  driverId: string | undefined,
  address: AddressRecord | undefined,
): Promise<{
  readonly distance: DistanceEtaResult | undefined;
  readonly locationSent: boolean;
  readonly locationRefused: boolean;
}> {
  let here: GeolocationPosition;
  try {
    here = await position();
  } catch {
    return { distance: undefined, locationSent: false, locationRefused: true };
  }

  let locationSent = false;
  if (driverId !== undefined) {
    try {
      await api.recordPosition({
        sampleId: globalThis.crypto.randomUUID(),
        driverId,
        deviceId,
        sequence: nextSequence(),
        latitude: here.coords.latitude,
        longitude: here.coords.longitude,
        /*
         * Required, and the browser may answer `null` for it. `0` would be a claim of perfect
         * accuracy, so an unknown accuracy is sent as the coordinate's own worst case rather than
         * its best: the service's staleness rule draws an accuracy circle, and a circle of radius
         * zero over an unknown position is the one shape that must not be drawn.
         */
        horizontalAccuracyMetres: here.coords.accuracy > 0 ? here.coords.accuracy : 10_000,
        observedAt: new Date(here.timestamp).toISOString(),
      });
      locationSent = true;
    } catch {
      locationSent = false;
    }
  }

  if (address === undefined) return { distance: undefined, locationSent, locationRefused: false };

  const distance = await api
    .distanceEta({
      origin: { latitude: here.coords.latitude, longitude: here.coords.longitude },
      destination: { latitude: address.latitude, longitude: address.longitude },
      mode: PICKUP_TRAVEL_MODE,
    })
    .catch(() => undefined);

  return { distance, locationSent, locationRefused: false };
}
